import { useState, useEffect } from 'react';
import Card from '../components/common/Card.jsx';
import Table from '../components/common/Table.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import VerticalBarChart from '../components/charts/VerticalBarChart.jsx';

import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { parseCost, exportCSV, formatSavingStr, buildExportFilename, buildBaseCostKpis, ADD_NEW_ACCOUNT, setScopedItem, STORAGE_KEYS } from '../components/common/Helper';
import { CostCell, RecommendedCostCell, SavingCell, RecCell } from '../components/optimization/RecCells.jsx';

import OptimizationService from '../services/OptimizationService';
import { useAuth } from '../state/AuthContext';

// ── Row Mapper ─────────────────────────────────────────────
const mapS3Rows = (data) =>
  data.map((item, index) => {
    const bucketName = item.BucketName ?? `s3-bucket-${index}`;

    // Costs directly from backend — no custom calculations
    const costValue          = parseCost(item.currentOnDemandMonthlyStoragePriceValue ?? 0);
    const recommendedCostValue = parseCost(item.recommendedOnDemandMonthlyStoragePriceValue ?? 0);
    const savingValue        = parseCost(item.savingCost ?? 0);

    // Tier comparison costs from backend (what each storage tier would cost)
    const standardCost = parseCost(item.StandardS3MonthlyCostValue ?? 0);
    const iaCost       = parseCost(item.InfrequentAccessMonthlyCostValue ?? 0);
    const glacierCost  = parseCost(item.GlacierMonthlyCostValue ?? 0);

    // Lifecycle from backend
    const lifecycle = item.LifecycleStatus
      ?? (Array.isArray(item.LifecycleConfiguration) && item.LifecycleConfiguration.length > 0 ? 'Configured' : 'None');

    const objects = item.BucketObjects ?? 0;
    const formatObjects = (n) => {
      const v = parseInt(n) || 0;
      return v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : String(v);
    };

    // Recommendation directly from backend field
    const rec = (item.recommendation ?? '').toUpperCase().trim();
    const recType = rec.replace(/_/g, '-') || 'OPTIMAL';

    let recPillClass = 'rec-ok';
    if (!rec || rec === 'OPTIMAL' || rec === 'OPTIMIZED' || rec === 'NONE' || rec === 'NA') {
      recPillClass = 'rec-ok';
    } else if (rec === 'DELETE' || rec === 'REMOVE' || rec === 'RELEASE' || rec === 'EXPIRE') {
      recPillClass = 'rec-id';
    } else if (rec === 'STOP' || rec === 'SCHEDULE') {
      recPillClass = 'rec-warn';
    } else {
      recPillClass = 'rec-rs';
    }

    return {
      id:                   bucketName,
      bucketName,
      region:               item.Region ?? '',
      storageClass:         item.StorageClass ?? 'Standard',
      size:                 item.BucketSize ?? '0 GB',
      objects:              formatObjects(objects),
      versioning:           item.S3VersioningStatus ?? 'Disabled',
      lifecycle,
      intelligentTieringEnabled: item.intelligentTieringEnabled ?? false,
      cost:                 `$${costValue.toFixed(2)}`,
      costValue,
      recommendedCost:      `$${recommendedCostValue.toFixed(2)}`,
      recommendedCostValue,
      saving:               formatSavingStr(savingValue),
      savingValue,
      recType,
      recPillClass,
      recommendationRemark: '',
      recLabel:             '',
      standardCost,
      iaCost,
      glacierCost,
      actionLabel:          'Assist with AI',
      actionClass:          'mini-btn ai-assist',
      checked:              false,
    };
  });

// ── KPI Builder ────────────────────────────────────────────
const buildKpiData = (totalPriceValue) => {
  const current    = parseCost(totalPriceValue?.currentCost   ?? 0);
  const saving     = parseCost(totalPriceValue?.savingCost    ?? 0);
  const recommended = parseCost(totalPriceValue?.recommendedCost ?? 0);
  const totalBuckets = totalPriceValue?.totalBuckets ?? 0;
  const totalStorage = totalPriceValue?.totalStorage ?? '0 GB';
  const totalObjects = (totalPriceValue?.totalObjects ?? 0).toLocaleString();
  return buildBaseCostKpis({
    countTitle:    'Total Buckets',
    countValue:    totalBuckets,
    countSubtitle: `${totalStorage} · ${totalObjects} objects`,
    current,
    saving,
    recommended,
  });
};

// ── Chart Builders (real backend data only) ────────────────

const buildTopBucketsChartData = (rows) => {
  const sorted = [...rows].filter((r) => r.costValue > 0).sort((a, b) => b.costValue - a.costValue).slice(0, 8);
  if (!sorted.length) return null;
  return {
    labels: sorted.map((r) => r.bucketName.length > 22 ? r.bucketName.slice(0, 22) + '…' : r.bucketName),
    datasets: [{
      data:            sorted.map((r) => parseFloat(r.costValue.toFixed(3))),
      backgroundColor: '#fbc02d',
      borderRadius:    4,
      barPercentage:   0.6,
    }],
  };
};

const buildSavingsByRegionChartData = (rows) => {
  const map = {};
  rows.forEach((r) => { if (r.region) map[r.region] = (map[r.region] ?? 0) + r.savingValue; });
  const entries = Object.entries(map).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return {
    labels: entries.map(([r]) => r),
    datasets: [{
      data:            entries.map(([, v]) => parseFloat(v.toFixed(3))),
      backgroundColor: '#2ea66f',
      borderRadius:    4,
      barPercentage:   0.6,
    }],
  };
};

const buildTierCostChartData = (rows) => {
  const std = rows.reduce((s, r) => s + r.standardCost, 0);
  const ia  = rows.reduce((s, r) => s + r.iaCost,       0);
  const gl  = rows.reduce((s, r) => s + r.glacierCost,  0);
  if (!std && !ia && !gl) return null;
  return {
    labels: ['Standard (Current)', 'Infrequent Access', 'Glacier'],
    datasets: [{
      data:            [parseFloat(std.toFixed(3)), parseFloat(ia.toFixed(3)), parseFloat(gl.toFixed(3))],
      backgroundColor: ['#fbc02d', '#2196f3', '#8e24aa'],
      borderRadius:    4,
      barPercentage:   0.5,
    }],
  };
};

const buildRecTypeChartData = (rows) => {
  const map = {};
  rows.forEach((r) => { const k = r.recType || 'OPTIMAL'; map[k] = (map[k] ?? 0) + 1; });
  const labels = Object.keys(map);
  if (!labels.length) return null;
  const colors = ['#fbc02d', '#2196f3', '#2ea66f', '#8e24aa', '#f57c00', '#ef4444'];
  return {
    labels,
    datasets: [{ data: labels.map((l) => map[l]), backgroundColor: colors, borderWidth: 2, borderColor: '#fff' }],
  };
};

const buildInsightActions = (rows) => {
  const withSaving = rows.filter((r) => r.savingValue > 0).sort((a, b) => b.savingValue - a.savingValue);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingValue, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →', actionClass = 'mini-btn';
    if (r.recType === 'INT-TIERING')       { actionLabel = 'Enable →'; }
    else if (r.recType === 'GLACIER')      { actionLabel = 'Apply policy →'; }
    else if (r.recType === 'EXPIRE')       { actionLabel = 'Apply →'; }
    return { id: i + 1, title: r.bucketName, amount: r.saving, desc: `${r.storageClass} · ${r.size} · ${r.recType}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

// ── Main Component ──────────────────────────────────────────
const S3Optimization = () => {
  const { selectedAccount } = useAuth();

  const [isLoading, setIsLoading]               = useState(false);
  const [rows, setRows]                         = useState([]);
  const [kpiData, setKpiData]                   = useState([]);

  const [topBucketsData, setTopBucketsData]     = useState(null);
  const [savingsByRegionData, setSavingsByRegionData] = useState(null);
  const [tierCostData, setTierCostData]         = useState(null);
  const [recTypeData, setRecTypeData]           = useState(null);
  const [insightActions, setInsightActions]     = useState([]);
  const [insightTotal, setInsightTotal]         = useState(0);

  const [regionFilter, setRegionFilter]         = useState('All regions');
  const [classFilter, setClassFilter]           = useState('All');
  const [versioningFilter, setVersioningFilter] = useState('All');

  const [aiOpen, setAiOpen]   = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData]   = useState(null);

  const handleDetailsClick = (row) => {
    setAiData({
      bucketName: row.bucketName, region: row.region, storageClass: row.storageClass,
      size: row.size, objects: row.objects, versioning: row.versioning,
      currentMonthlyCost: row.cost, recommendedMonthlyCost: row.recommendedCost,
      recommendation: row.recType, estimatedMonthlySaving: row.saving,
    });
    setAiPrompt('You are a cloud cost advisor. Based on the S3 bucket data, give a short, friendly insight about cost situation, the recommended action, and potential monthly savings.');
    setAiOpen(true);
  };

  const fetchData = (isForceRefresh = false) => {
    if (!selectedAccount || selectedAccount === ADD_NEW_ACCOUNT || isLoading) return;
    setIsLoading(true);
    setRows([]);
    OptimizationService.getTabularData(selectedAccount, 'S3', null, isForceRefresh)
      .then((data) => {
        const mapped = mapS3Rows(data?.rows ?? []);
        setRows(mapped);
        setKpiData(buildKpiData(data.totalPriceValue ?? {}));
      })
      .catch(() => setKpiData(buildKpiData({})))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => { if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) fetchData(); }, [selectedAccount]);

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'S3 Optimization',
      kpi: kpiData,
      s3Buckets: rows,
    });
  }, [rows, kpiData]);

  const filteredRows = rows.filter((row) => {
    if (regionFilter !== 'All regions' && row.region !== regionFilter) return false;
    if (classFilter !== 'All' && !row.storageClass.toLowerCase().includes(classFilter.toLowerCase())) return false;
    if (versioningFilter !== 'All' && row.versioning !== versioningFilter) return false;
    return true;
  });

  useEffect(() => {
    if (filteredRows.length > 0) {
      setTopBucketsData(buildTopBucketsChartData(filteredRows));
      setSavingsByRegionData(buildSavingsByRegionChartData(filteredRows));
      setTierCostData(buildTierCostChartData(filteredRows));
      setRecTypeData(buildRecTypeChartData(filteredRows));
      const { actions, totalSaving } = buildInsightActions(filteredRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setTopBucketsData(null); setSavingsByRegionData(null); setTierCostData(null); setRecTypeData(null);
      setInsightActions([]); setInsightTotal(0);
    }
  }, [rows, regionFilter, classFilter, versioningFilter]);

  const selectedCount  = filteredRows.filter((r) => r.checked).length;
  const selectedSaving = filteredRows.filter((r) => r.checked).reduce((s, r) => s + r.savingValue, 0);
  const allChecked     = filteredRows.length > 0 && filteredRows.every((r) => r.checked);

  const toggleRowChecked = (id) => setRows((prev) => prev.map((r) => r.id === id ? { ...r, checked: !r.checked } : r));
  const toggleAllChecked = () => { const v = !allChecked; setRows((prev) => prev.map((r) => ({ ...r, checked: v }))); };

  const s3Columns = [
    {
      key: 'checkbox',
      label: <span className={`checkbox ${allChecked ? 'checked' : ''}`} onClick={toggleAllChecked} />,
      className: 'center',
      render: (row) => <span className={`checkbox ${row.checked ? 'checked' : ''}`} onClick={(e) => { e.stopPropagation(); toggleRowChecked(row.id); }} />,
    },
    { key: 'bucketName',   label: 'Bucket Name' },
    { key: 'region',       label: 'Region' },
    { key: 'storageClass', label: 'Storage Class' },
    { key: 'size',         label: 'Size',    className: 'num' },
    { key: 'objects',      label: 'Objects', className: 'num' },
    { key: 'versioning',   label: 'Versioning' },
    { key: 'cost',         label: 'Cost / Mo',        className: 'num', render: (row) => <CostCell row={row} /> },
    { key: 'recommendationRemark', label: 'Recommendation',  render: (row) => <RecCell row={row} /> },
    { key: 'saving',       label: 'Saving / Mo',      className: 'num', render: (row) => <SavingCell row={row} /> },
    { key: 'recommendedCost', label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> },
    { key: 'action',       label: 'Action', render: (row) => <button className={row.actionClass} onClick={() => handleDetailsClick(row)}>{row.actionLabel}</button> },
  ];

  const handleExport = () =>
    exportCSV(
      filteredRows,
      s3Columns.filter((c) => typeof c.label === 'string' && !['checkbox', 'action'].includes(c.key)),
      buildExportFilename('s3_storage_optimization', selectedAccount),
    );

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>S3</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            S3 Storage Optimization{' '}
            {isLoading ? <BadgeSkeleton /> : <span className="badge">{filteredRows.length} buckets</span>}
          </h1>
          <div className="meta">Tier infrequent data, enable Intelligent-Tiering, and optimise S3 storage costs</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={filteredRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={() => fetchData(true)} disabled={isLoading}>↻ {isLoading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <KpiRow kpiData={kpiData} loading={isLoading} />

      <div className="grid-3">
        <Card className="first" title="Top buckets by cost" subtitle="Current monthly storage cost · top 8">
          <HorizontalBarChart data={topBucketsData} loading={isLoading} />
        </Card>
        <Card className="first" title="Savings by region" subtitle="Potential monthly saving per region">
          <VerticalBarChart data={savingsByRegionData} loading={isLoading} yAxisPrefix="$" />
        </Card>
        <TopActionsCard
          title="Top S3 actions"
          actions={insightActions}
          total={insightTotal}
          loading={isLoading}
          entityName="buckets"
          onAction={(item) => item.row && handleDetailsClick(item.row)}
        />
      </div>

      <div className="filter-bar">
        <div className="field">
          <label>Region</label>
          <select value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)}>
            <option value="All regions">All regions</option>
            {[...new Set(rows.map((r) => r.region).filter(Boolean))].sort().map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Storage Class</label>
          <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            <option value="All">All</option>
            {[...new Set(rows.map((r) => r.storageClass).filter(Boolean))].sort().map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Versioning</label>
          <select value={versioningFilter} onChange={(e) => setVersioningFilter(e.target.value)}>
            <option value="All">All</option>
            <option value="Enabled">Enabled</option>
            <option value="Disabled">Disabled</option>
          </select>
        </div>
        <div className="spacer" />
        <button className="btn ghost" onClick={() => { setRegionFilter('All regions'); setClassFilter('All'); setVersioningFilter('All'); }}>✕ Clear</button>
        <button className="btn primary" onClick={() => fetchData(true)} disabled={isLoading}>↻ {isLoading ? 'Refreshing…' : 'Refresh'}</button>
      </div>

      {selectedCount > 0 && (
        <div className="bulk-bar">
          <span><strong>{selectedCount} buckets selected</strong> · ${selectedSaving.toFixed(2)}/mo combined savings</span>
          <button className="btn ghost">🧊 Enable Intelligent-Tiering</button>
          <button className="btn ghost">📋 Apply Lifecycle</button>
          <button className="btn primary">⚡ Apply</button>
        </div>
      )}

      <Table
        columns={s3Columns}
        rows={filteredRows}
        loading={isLoading}
        defaultPageSize={25}
        defaultSortKey="costValue"
        defaultSortDir="desc"
        showToolbar={true}
        onExport={handleExport}
        emptyText="No S3 buckets found."
        subscriptionGated
      />

      <div className="grid-2 grid-gap-top">
        <Card className="first" title="Storage tier cost comparison" subtitle="Total cost across all buckets per storage class option">
          <VerticalBarChart data={tierCostData} loading={isLoading} yAxisPrefix="$" />
        </Card>
        <Card className="first" title="Recommendation distribution" subtitle="Buckets by recommendation type">
          <DonutChart data={recTypeData} loading={isLoading} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />
    </>
  );
};

export default S3Optimization;
